import { type Bill, db, type MosDB, newId, type Transaction } from "./db";
import { createTransaction, deleteTransaction } from "./finance";
import { assertUGX } from "./money";
import { formatRule, nextDueDate, parseRule } from "./recurrence";
import { nextUpdatedAt, remove, save, update } from "./store";

// Recurring bills (FN-9). A bill is a template; paying it logs an ordinary
// expense and moves next_due on by its rule, both in one local transaction,
// so they sync together and work offline. Push reminders come in Phase 6.

export class BillError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "BillError";
	}
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export type NewBill = Pick<Bill, "name" | "amount" | "wallet_id" | "recurrence_rule" | "next_due"> &
	Partial<Pick<Bill, "category_id" | "archived">>;

/**
 * Validates a bill and returns it in canonical form. Monthly rules get the
 * day of month pinned from next_due (BYMONTHDAY), so rent due on the 31st
 * comes back to the 31st after a short month instead of drifting to the 28th.
 */
function normalise<T extends Pick<Bill, "name" | "amount" | "recurrence_rule" | "next_due">>(
	bill: T,
): T {
	const name = bill.name.trim();
	if (name.length < 1 || name.length > 50) throw new BillError("Name must be 1-50 characters");
	assertUGX(bill.amount);
	if (bill.amount <= 0) throw new BillError("Amount must be more than zero");
	if (!DAY.test(bill.next_due)) throw new BillError("Pick the next due date");
	const rule = parseRule(bill.recurrence_rule);
	if (!rule) throw new BillError("Pick how often it repeats");
	if (rule.freq === "monthly" && rule.byMonthDay === undefined) {
		rule.byMonthDay = Number(bill.next_due.slice(8, 10));
	}
	return { ...bill, name, recurrence_rule: formatRule(rule) };
}

export async function createBill(input: NewBill, database: MosDB = db): Promise<Bill> {
	const now = nextUpdatedAt();
	const bill = normalise({
		id: newId(),
		category_id: null,
		archived: false,
		...input,
		created_at: now,
		updated_at: now,
		deleted_at: null,
	});
	return save("bills", bill, database);
}

export async function updateBill(
	id: string,
	patch: Partial<Omit<Bill, "id" | "created_at" | "updated_at" | "deleted_at">>,
	database: MosDB = db,
): Promise<Bill> {
	const current = await database.bills.get(id);
	if (!current || current.deleted_at) throw new BillError("Bill not found");
	const next = normalise({ ...current, ...patch });
	return update(
		"bills",
		id,
		{ ...patch, name: next.name, recurrence_rule: next.recurrence_rule },
		database,
	);
}

/** Archived bills stop showing as due; restore with archived = false. */
export function archiveBill(id: string, archived = true, database: MosDB = db): Promise<Bill> {
	return update("bills", id, { archived }, database);
}

export function deleteBill(id: string, database: MosDB = db): Promise<void> {
	return remove("bills", id, database);
}

/** Live, unarchived bills due on or before `today` (YYYY-MM-DD), oldest first. */
export function dueBills(bills: Bill[], today: string): Bill[] {
	return bills
		.filter((b) => !b.deleted_at && !b.archived && b.next_due <= today)
		.sort((a, b) => a.next_due.localeCompare(b.next_due) || a.name.localeCompare(b.name));
}

export type Payment = {
	bill: Bill;
	transaction: Transaction;
	/** next_due before paying, for undo. */
	paidDue: string;
};

/**
 * Pays one occurrence: logs the expense (note = bill name, from the bill's
 * wallet and category) and moves next_due one period on from the occurrence
 * paid, not from today, so an overdue bill paid late keeps its schedule.
 */
export async function payBill(
	id: string,
	opts: { now?: Date; database?: MosDB } = {},
): Promise<Payment> {
	const database = opts.database ?? db;
	const occurredAt = (opts.now ?? new Date()).toISOString();
	return database.transaction(
		"rw",
		[database.bills, database.transactions, database.outbox],
		async () => {
			const bill = await database.bills.get(id);
			if (!bill || bill.deleted_at) throw new BillError("Bill not found");
			if (bill.archived) throw new BillError("This bill is archived");

			const transaction = await createTransaction(
				{
					kind: "expense",
					amount: bill.amount,
					wallet_id: bill.wallet_id,
					category_id: bill.category_id,
					note: bill.name,
					occurred_at: occurredAt,
				},
				database,
			);
			const next = await update(
				"bills",
				id,
				{ next_due: nextDueDate(bill.recurrence_rule, bill.next_due) },
				database,
			);
			return { bill: next, transaction, paidDue: bill.next_due };
		},
	);
}

/** Reverses payBill: removes the expense and puts next_due back. */
export async function undoPayment(payment: Payment, database: MosDB = db): Promise<void> {
	await database.transaction(
		"rw",
		[database.bills, database.transactions, database.outbox],
		async () => {
			await deleteTransaction(payment.transaction.id, database);
			await update("bills", payment.bill.id, { next_due: payment.paidDue }, database);
		},
	);
}
